// "Unde este documentul?" — registration, distribution, each department's status and the time left until the deadline.
import React from 'react';
import type { Approval, Assignment, Doc } from '../types';
import { STATUS_LABEL, daysUntil } from '../types';
import { DaysLeft, Modal, Stamp, StatusChip, fmtDate, fmtDateTime } from '../ui';
import { Icon } from '../ui/Icon';

const fixTs = (iso?: string | null) => (iso ? iso.replace(/([+-]\d{2})$/, '$1:00') : iso);
const dt = (iso?: string | null) => (iso ? fmtDateTime(fixTs(iso)) : '');

export function DeadlineMeter({ doc }: { doc: Doc }) {
  if (!doc.deadline) return <div className="dl-meter none"><Icon name="clock" size={20} /><div><b>Fără termen</b><div className="muted small">Termenul se calculează din Tipul documentului (se poate seta și manual la modificare).</div></div></div>;
  const n = daysUntil(doc.deadline)!;
  const resolved = doc.status === 'RESOLVED';
  const start = new Date(fixTs(doc.createdAt) || doc.createdAt).getTime();
  const end = new Date(doc.deadline.slice(0, 10) + 'T23:59:59').getTime();
  const now = resolved && doc.resolvedAt ? new Date(fixTs(doc.resolvedAt)!).getTime() : Date.now();
  const pct = Math.max(0, Math.min(100, ((now - start) / Math.max(1, end - start)) * 100));
  const cls = resolved ? 'ok' : n < 0 ? 'over' : n <= 10 ? 'soon' : 'ok';
  return (
    <div className={'dl-meter ' + cls}>
      <div className="dl-big">
        <span className="dl-num">{resolved ? <Icon name="check" size={30} /> : Math.abs(n)}</span>
        <span className="dl-unit">{resolved ? 'rezolvat' : n < 0 ? (Math.abs(n) === 1 ? 'zi depășire' : 'zile depășire') : n === 0 ? 'expiră azi' : n === 1 ? 'zi rămasă' : 'zile rămase'}</span>
      </div>
      <div className="dl-info">
        <div className="row" style={{ justifyContent: 'space-between' }}><span className="muted small">Înregistrat {fmtDate(doc.createdAt.slice(0, 10))}</span><b>Termen: {fmtDate(doc.deadline)}</b></div>
        <div className="dl-bar"><div className="dl-fill" style={{ width: pct + '%' }} /></div>
        <div className="muted small">{resolved ? `Rezolvat pe ${dt(doc.resolvedAt)}${doc.resolvedAt && new Date(fixTs(doc.resolvedAt)!).getTime() > end ? ' — după termen' : ' — în termen'}` : n < 0 ? 'Termenul a expirat.' : `Au trecut ${Math.round(pct)}% din timpul disponibil.`}</div>
      </div>
    </div>
  );
}

/** Approvals of an assignment's latest round, in hierarchical order. */
export function latestChain(doc: Doc, a: Assignment): Approval[] {
  const all = (doc.approvals || []).filter(x => x.assignmentId === a.id);
  if (!all.length) return [];
  const round = Math.max(...all.map(x => x.round));
  return all.filter(x => x.round === round).sort((x, y) => x.step - y.step);
}

const STEP_LABEL: Record<Approval['status'], string> = { WAITING: 'în așteptare', PENDING: 'de aprobat acum', APPROVED: 'aprobat', REJECTED: 'respins', CANCELLED: 'anulat' };
const STEP_ICON: Record<Approval['status'], string> = { WAITING: 'clock', PENDING: 'approve', APPROVED: 'check', REJECTED: 'x', CANCELLED: 'x' };

/** Hierarchical approval chain: preparer → unit head → … → top of the org chart. */
export function ApprovalChain({ doc, a, preview }: { doc: Doc; a: Assignment; preview?: boolean }) {
  const chain = latestChain(doc, a);
  const rejected = chain.find(x => x.status === 'REJECTED');
  if (!chain.length) {
    if (preview && a.approvalPreview && a.approvalPreview.length && a.status === 'IN_WORK')
      return <div className="chain" title="Traseul de aprobare, conform organigramei"><span className="muted small">Aprobare:</span>{a.approvalPreview.map((n, i) => <React.Fragment key={i}>{i > 0 && <span className="sep">›</span>}<span className="chain-step WAITING">{n}</span></React.Fragment>)}</div>;
    return null;
  }
  return (
    <div>
      <div className="chain">
        {a.preparedByName && <><span className="chain-step APPROVED" title={'Răspuns pregătit ' + dt(a.preparedAt)}><Icon name="edit" size={12} />{a.preparedByName}</span><span className="sep">›</span></>}
        {chain.map((x, i) => (
          <React.Fragment key={x.id}>
            {i > 0 && <span className="sep">›</span>}
            <span className={'chain-step ' + x.status} title={`${x.departmentName} — ${STEP_LABEL[x.status]}${x.decidedByName ? ` de ${x.decidedByName}, ${dt(x.decidedAt)}` : x.approverNames ? ` (șef: ${x.approverNames})` : ''}`}>
              <Icon name={STEP_ICON[x.status]} size={12} />{x.departmentName}{x.decidedByName ? ` · ${x.decidedByName}` : x.approverNames && x.status === 'PENDING' ? ` · ${x.approverNames}` : ''}
            </span>
          </React.Fragment>
        ))}
        {(a.round || 0) > 1 && <span className="muted small" style={{ marginLeft: 6 }}>runda {a.round}</span>}
      </div>
      {rejected && a.status === 'IN_WORK' && <div className="reject-note"><b>Respins de {rejected.decidedByName} ({rejected.departmentName}):</b> {rejected.comment}</div>}
    </div>
  );
}

export function StatusWindow({ doc, onClose, onDeptStatus, onDecide, isAdmin }: { doc: Doc; onClose: () => void; onDeptStatus: (a: Assignment, status: 'IN_WORK' | 'RESOLVED', force?: boolean) => void; onDecide?: (ap: Approval, d: 'APPROVE' | 'REJECT') => void; isAdmin?: boolean }) {
  const as = doc.assignments || [];
  const answered = doc.status === 'RESOLVED' || doc.status === 'TO_DISTRIBUTE' || doc.status === 'FINAL_APPROVAL';
  const done = as.filter(a => a.status === 'RESOLVED').length;
  const inApproval = as.filter(a => a.status === 'APPROVAL').length;
  const firstAssigned = as.map(a => a.assignedAt).filter(Boolean).sort()[0];
  return (
    <Modal title={<span className="row" style={{ gap: 10 }}>Situația documentului <Stamp value={doc.regFull} /></span>} icon="clock" size="lg" onClose={onClose}
      footer={<><span className="muted small">{as.length ? `${done} din ${as.length} compartimente au rezolvat${inApproval ? ` · ${inApproval} în aprobare` : ''}` : 'Documentul nu a fost încă repartizat'}</span><span className="grow" /><button className="btn" onClick={onClose}>Închide</button></>}>
      <div className="stack" style={{ gap: 18 }}>
        <DeadlineMeter doc={doc} />

        <div className="sw-steps">
          <div className="sw-step done">
            <span className="sw-dot"><Icon name="inbox" size={15} /></span>
            <div><b>Primit în registratură</b><div className="muted small">{dt(doc.createdAt)} · {doc.createdByName}</div></div>
          </div>
          <div className={'sw-step ' + (as.length ? 'done' : 'todo')}>
            <span className="sw-dot"><Icon name="send" size={15} /></span>
            <div><b>{as.length ? `Distribuit către ${as.length} ${as.length === 1 ? 'compartiment' : 'compartimente'}` : 'Nedistribuit încă'}</b><div className="muted small">{firstAssigned ? dt(firstAssigned) : 'Se distribuie din „Modifică” → Compartimentul / Persoana'}</div></div>
          </div>
          <div className={'sw-step ' + (answered || as.some(a => a.status !== 'IN_WORK') ? 'done' : 'todo')}>
            <span className="sw-dot"><Icon name="approve" size={15} /></span>
            <div><b>Aprobare răspuns pe ierarhie</b><div className="muted small">{inApproval ? `${inApproval} ${inApproval === 1 ? 'răspuns în aprobare' : 'răspunsuri în aprobare'} la șefii ierarhici` : answered ? 'Răspunsurile au fost aprobate' : 'După ce compartimentul pregătește răspunsul, acesta urcă secvențial la șefii din organigramă'}</div></div>
          </div>
          <div className={'sw-step ' + (answered ? 'done' : 'todo')}>
            <span className="sw-dot"><Icon name="check" size={15} /></span>
            <div><b>{answered ? 'Rezolvat de toate compartimentele' : 'În așteptarea rezolvării'}</b><div className="muted small">{answered ? (doc.readyAt ? dt(doc.readyAt) : doc.resolvedAt ? dt(doc.resolvedAt) : '') : `Fiecare compartiment trebuie să-și rezolve partea · status: ${STATUS_LABEL[doc.status]}`}</div></div>
          </div>
          {doc.isRegistry && (
            <div className={'sw-step ' + (doc.status === 'RESOLVED' ? 'done' : 'todo')}>
              <span className="sw-dot"><Icon name="send" size={15} /></span>
              <div><b>{doc.status === 'RESOLVED' ? (doc.portalRequestId ? 'Aprobat de conducere — transmis în portal' : 'Distribuit — finalizat') : doc.status === 'FINAL_APPROVAL' ? 'La aprobarea conducerii' : doc.status === 'TO_DISTRIBUTE' ? 'Spre distribuire la registratură' : doc.portalRequestId ? 'Aprobarea conducerii și transmiterea în portal' : 'Distribuirea răspunsului'}</b>
                <div className="muted small">{doc.status === 'RESOLVED' ? `${doc.distribution?.date ? fmtDate(doc.distribution.date) : dt(doc.resolvedAt)} · ${doc.distribution?.byName || doc.resolvedByName || ''}${doc.distribution?.recipient ? ' · către ' + doc.distribution.recipient : ''}` : 'Registratura trimite răspunsul și finalizează documentul'}</div></div>
            </div>
          )}
        </div>

        {as.length > 0 && (
          <div>
            <div className="section-title">Compartimente</div>
            <div className="sw-deps">
              {as.map(a => {
                const answers = (doc.responseFiles || []).filter(f => f.departmentId != null ? f.departmentId === a.departmentId : f.departmentName === a.departmentName);
                const canAct = a.mine || doc.canManage;
                return (
                  <div key={a.id} className={'sw-dep ' + (a.status === 'RESOLVED' ? 'resolved' : a.status === 'APPROVAL' ? 'approval' : 'working')}>
                    <div className="sw-dep-head">
                      <Icon name="users" size={16} />
                      <b>{a.departmentName || a.userName}</b>{a.userName && a.departmentName && <span className="muted small">· {a.userName}</span>}
                      <span className="grow" />
                      <StatusChip status={a.status} part />
                    </div>
                    <div className="sw-dep-body">
                      <span><span className="muted">Primit:</span> {dt(a.assignedAt)}</span>
                      {a.status === 'RESOLVED'
                        ? <span><span className="muted">Rezolvat:</span> {dt(a.resolvedAt)} · {a.resolvedByName}</span>
                        : <span><span className="muted">Termen:</span> {doc.deadline ? <>{fmtDate(doc.deadline)} <DaysLeft deadline={doc.deadline} inline /></> : '—'}</span>}
                      <span><span className="muted">Răspunsuri:</span> {answers.length ? answers.map(f => f.name).join(', ') : '—'}</span>
                    </div>
                    <ApprovalChain doc={doc} a={a} preview={!!a.mine} />
                    {latestChain(doc, a).filter(x => x.status === 'PENDING' && x.canDecide).map(x => onDecide && (
                      <div key={x.id} className="sw-dep-actions">
                        <span className="small">Aveți de aprobat ca șef <b>{x.departmentName}</b></span><span className="grow" />
                        <button className="btn sm" onClick={() => onDecide(x, 'REJECT')}><Icon name="x" />Respinge</button>
                        <button className="btn sm primary" onClick={() => onDecide(x, 'APPROVE')}><Icon name="check" />Aprobă</button>
                      </div>
                    ))}
                    {canAct && (
                      <div className="sw-dep-actions">
                        {a.status === 'RESOLVED' && <button className="btn sm" onClick={() => onDeptStatus(a, 'IN_WORK')}><Icon name="refresh" />Redeschide pentru {a.departmentName}</button>}
                        {a.status === 'APPROVAL' && a.mine && <button className="btn sm" onClick={() => onDeptStatus(a, 'IN_WORK')}><Icon name="refresh" />Retrage din aprobare</button>}
                        {a.status === 'IN_WORK' && a.mine && <button className="btn sm primary" onClick={() => onDeptStatus(a, 'RESOLVED')}><Icon name={a.approvalPreview?.length ? 'send' : 'check'} />{a.approvalPreview?.length ? 'Trimite răspunsul spre aprobare' : `Marchează rezolvat pentru ${a.departmentName}`}</button>}
                        {a.status !== 'RESOLVED' && isAdmin && !a.mine && <button className="btn sm ghost" onClick={() => onDeptStatus(a, 'RESOLVED', true)} title="Doar administratorul: închide fără aprobări"><Icon name="check" />Rezolvă direct (admin)</button>}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
